import { Fragment, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import QAVideoManager from "@/pages/tuition/QAVideoManager";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Library, CheckCircle2, CircleDashed, RefreshCw, BookOpen, Lock, Info, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

export default function TeacherTuitionLibrary({ embedded } = {}) {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [classSel, setClassSel] = useState("");
  const [sectionSel, setSectionSel] = useState("");
  const [subjectSel, setSubjectSel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tuition/library/teacher");
      const g = data.groups || [];
      setGroups(g);
      // sensible defaults
      if (g.length) {
        const firstClass = classSel && g.find((x) => x.class_name === classSel) ? classSel : g[0].class_name;
        setClassSel(firstClass);
      }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load library"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const classes = useMemo(() => groups.map((g) => g.class_name), [groups]);
  const sectionsForClass = useMemo(() => {
    const secs = (user?.assignments || []).filter((a) => a.class_name === classSel).map((a) => a.section);
    const uniq = [...new Set(secs)].sort();
    return uniq.length ? uniq : ["A"];
  }, [user, classSel]);
  const currentGroup = useMemo(() => groups.find((g) => g.class_name === classSel), [groups, classSel]);
  const subjects = currentGroup?.subjects || [];
  const currentSubject = useMemo(() => subjects.find((s) => s.id === subjectSel) || subjects[0], [subjects, subjectSel]);

  // keep section/subject valid when class changes
  useEffect(() => { setSectionSel((prev) => (sectionsForClass.includes(prev) ? prev : sectionsForClass[0] || "")); }, [sectionsForClass]);
  useEffect(() => { setSubjectSel((prev) => (subjects.find((s) => s.id === prev) ? prev : subjects[0]?.id || "")); }, [classSel, subjects]);

  const toggle = async (lesson, value) => {
    if (value && !lesson.indexed) {
      toast.error("Upload a PDF for this lesson before enabling it for students.");
      return;
    }
    setSavingId(lesson.id);
    setGroups((prev) => prev.map((g) => ({
      ...g,
      subjects: g.subjects.map((s) => ({ ...s, lessons: s.lessons.map((l) => l.id === lesson.id ? { ...l, student_enabled: value } : l) })),
    })));
    try {
      await api.patch(`/tuition/library/teacher/lessons/${lesson.id}/visibility`, { student_enabled: value });
      toast.success(value ? "Lesson enabled for students." : "Lesson hidden from students.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update");
      setGroups((prev) => prev.map((g) => ({
        ...g,
        subjects: g.subjects.map((s) => ({ ...s, lessons: s.lessons.map((l) => l.id === lesson.id ? { ...l, student_enabled: !value } : l) })),
      })));
    } finally { setSavingId(null); }
  };

  const lessons = currentSubject?.lessons || [];

  const downloadLba = async (lesson) => {
    try {
      const res = await api.get(`/tuition/library/lessons/${lesson.id}/lba`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not open LBA PDF");
    }
  };

  const Wrap = embedded ? Fragment : Layout;
  return (
    <Wrap>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Knowledge base</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Tuition Library</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">Enable lessons for your students. A lesson can only be enabled once its PDF has been uploaded by admin.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="teacher-lib-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading library…</div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="teacher-lib-empty">
          No subjects available for your assigned classes yet.
        </div>
      ) : (
        <>
          {/* Selectors */}
          <div className="flex items-end gap-3 flex-wrap mb-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
              <Select value={classSel} onValueChange={setClassSel}>
                <SelectTrigger className="h-11 rounded-xl w-32" data-testid="teacher-lib-class"><SelectValue /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Section</label>
              <Select value={sectionSel} onValueChange={setSectionSel}>
                <SelectTrigger className="h-11 rounded-xl w-28" data-testid="teacher-lib-section"><SelectValue /></SelectTrigger>
                <SelectContent>{sectionsForClass.map((s) => <SelectItem key={s} value={s}>Section {s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Subject</label>
              <Select value={subjectSel} onValueChange={setSubjectSel}>
                <SelectTrigger className="h-11 rounded-xl w-56" data-testid="teacher-lib-subject"><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-400 mb-4" data-testid="teacher-lib-note">
            <Info className="h-3.5 w-3.5" /> Enabling a lesson makes it visible to all sections of Class {classSel}. Lessons without a PDF stay locked.
          </div>

          {/* Lessons for selected subject */}
          {!currentSubject ? (
            <div className="text-slate-500">Select a subject to manage its lessons.</div>
          ) : (
            <div className="border border-slate-200 dark:border-slate-700 rounded-2xl bg-white dark:bg-slate-800 px-5" data-testid="teacher-lib-lessons">
              <div className="flex items-center justify-between py-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center"><Library className="h-4 w-4" /></div>
                  <div>
                    <div className="text-base font-semibold text-slate-900 dark:text-slate-100">{currentSubject.name}</div>
                    <div className="text-xs text-slate-500">{currentSubject.indexed_count} of {currentSubject.total_count} lesson(s) have a PDF</div>
                  </div>
                </div>
              </div>
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {lessons.length === 0 ? (
                  <li className="py-6 text-center text-sm text-slate-500">No lessons yet.</li>
                ) : lessons.map((l) => {
                  const enabled = l.student_enabled === true;
                  const locked = !l.indexed;
                  return (
                    <li key={l.id} className="py-3 flex items-center justify-between gap-3" data-testid={`teacher-lib-lesson-${l.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${l.indexed ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-400 dark:bg-slate-700"}`}>
                          {l.indexed ? <CheckCircle2 className="h-4 w-4" /> : <CircleDashed className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{l.title}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1"><BookOpen className="h-3 w-3" /> {l.indexed ? "PDF uploaded" : "No PDF yet"}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <QAVideoManager
                          lessonId={l.id}
                          lessonTitle={l.title}
                          count={(l.video_qa || []).length}
                          onChange={load}
                        />
                        {l.lba_file_id && (
                          <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => downloadLba(l)} data-testid={`teacher-lib-lba-${l.id}`}>
                            <FileCheck2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> LBA
                          </Button>
                        )}
                        {locked ? (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-testid={`teacher-lib-locked-${l.id}`}>
                            <Lock className="h-3.5 w-3.5" /> Upload PDF to enable
                          </span>
                        ) : (
                          <span className={`text-xs font-medium ${enabled ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400"}`}>{enabled ? "Visible" : "Hidden"}</span>
                        )}
                        <Switch checked={enabled} disabled={locked || savingId === l.id} onCheckedChange={(v) => toggle(l, v)} data-testid={`teacher-lib-toggle-${l.id}`} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </Wrap>
  );
}
